import { computed, defineComponent, onMounted, onUnmounted, PropType, ref, watch } from "vue";
import { OfferJson } from "../../OrderForm";
import SmeLayout from "../../layouts/SmeLayout";
import UploadBox from "../../components/UploadBox";
import SmeOfferBox, { SmeBigBenefits } from "../../components/SmeOfferBox";
import SmeOffers from "../../components/SmeOffers";
import Utils from "../../utils/Utils";
import OfferBoxDocuments from "../../components/OfferBoxDocuments";
import { SmeOrderJson } from "../../utils/SmeUtils";
import Dialog from "../../utils/Dialog";
import SmeContactBox from "../../components/SmeContactBox";
import SmeSupportForm from "../../components/SmeSupportForm";
import Tracking from "../../utils/Tracking";
import OfferUtils from "../../utils/OfferUtils";

const SmeCompareTable = defineComponent({
    template: `
        <div class="SmeCompareTable">
            <div class="SmeCompareTable__row SmeCompareTable__row--head">
                <div class="SmeCompareTable__col">
                    {{ prev_vendor.label }}
                </div>

                <div class="SmeCompareTable__col">
                    Respect Energy
                </div>
            </div>

            <div class="SmeCompareTable__info">
                <div class="SmeCompareTable__label">Cena energii netto</div>

                <div class="SmeCompareTable__row">
                    <div class="SmeCompareTable__col" v-html="priceformat(options.savings.vendorprice, 3, 'zł/kWh')"></div>

                    <div class="SmeCompareTable__col SmeCompareTable__col--green" v-html="priceformat(options.savings.reprice, 3, 'zł/kWh')"></div>
                </div>
            </div>

            <div class="SmeCompareTable__info">
                <div class="SmeCompareTable__label">Miesięczne opłaty handlowe</div>

                <div class="SmeCompareTable__row">
                    <div class="SmeCompareTable__col" v-html="options.savings.vendorfee !== null ? priceformat(options.savings.vendorfee, 2, 'zł') : 'brak danych'"></div>

                    <div class="SmeCompareTable__col SmeCompareTable__col--green" v-html="priceformat(options.savings.refee, 2, 'zł')"></div>
                </div>
            </div>

            <div class="SmeCompareTable__info">
                <div class="SmeCompareTable__label">Szacunkowy miesięczny koszt energii</div>

                <div class="SmeCompareTable__row">
                    <div class="SmeCompareTable__col" v-html="priceformat(options.savings.vendormonth, 0, 'zł')"></div>

                    <div class="SmeCompareTable__col SmeCompareTable__col--green" v-html="priceformat(options.savings.remonth, 0, 'zł')"></div>
                </div>
            </div>

            <div v-if="offer.cashback" class="SmeCompareTable__info">
                <div class="SmeCompareTable__label">Dodatkowy bonus</div>

                <div class="SmeCompareTable__row">
                    <div class="SmeCompareTable__col">
                        {{ priceformat(0, 0) }}
                    </div>

                    <div class="SmeCompareTable__col SmeCompareTable__col--green">
                        {{ priceformat(offer.cashback, 0) }}
                    </div>
                </div>
            </div>
        </div>
    `,

    props: {
        offer: { type: Object as PropType<OfferJson>, required: true },
        options: { type: Object as PropType<SmeOrderJson>, required: true },
    },

    setup(props, ctx) {
        const prev_vendor = computed(() => {
            return props.options.si_vendors?.find(v => v.id == props.options.prev_vendor);
        });

        function priceformat(val, round: number, unit: string) {
            return Utils.priceFormat(val, round == 0, unit ? ' <span>' + unit + '</span>' : '', round);
        }

        return {
            prev_vendor,

            priceformat,
        };
    },
});

export default defineComponent({
    components: {
        SmeLayout,
        UploadBox,
        SmeOffers,
        SmeOfferBox,
        SmeBigBenefits,
        OfferBoxDocuments,
        SmeCompareTable,
        Dialog,
        SmeContactBox,
        SmeSupportForm,
    },

    template: `
        <SmeLayout class="StepSmeOffers" wrapper="1104" :class="{ 'StepSmeOffers--loading': loading }">
            <SmeContactBox v-if="mobile && bigusage" title="Masz duże zużycie energii!" content="Skontaktuj się z naszym Doradcą, który zaproponuje Ci indywidualne rozwiązanie lub skorzystaj z gotowej oferty online." :button2="true" @click:button2="onOfferClick" />
            <SmeContactBox v-if="mobile && !options.savings && options.tariff == 'C11'" title="Twoja obecna cena energii nie jest zła!" content="Wybierając Respect Energy, możesz zamrozić stawkę nawet na kolejne 10 lat. <b>Przy dłuższych kontraktach oferujemy niższe ceny!</b><br><br>Ponadto możesz cieszyć się brakiem opłat handlowych, wydłużonymi terminami płatności i 200 zł zniżki na ofertę dla domu." :button="false" :button2="true" button2_label="Sprawdź ofertę" @click:button2="onOfferClick" />

            <h1 class="title-1">Kalkulacja dla Twojej Firmy</h1>

            <div v-if="mobile" class="StepSmeOffers__usage-amount">
                <div>dla szacowanego zużycia <b>{{ usageformat }} kWh/rok<sup>*</sup></b></div>

                <button type="button" @click="onUsageClick">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M1.7002 4.3702V19.6302C1.7002 21.1002 2.9002 22.3002 4.3702 22.3002H19.6302C21.1002 22.3002 22.3002 21.1002 22.3002 19.6302V9.9802C22.3002 9.54019 21.9402 9.1802 21.5002 9.1802C21.0602 9.1802 20.7002 9.54019 20.7002 9.9802V19.6302C20.7002 20.2202 20.2202 20.7002 19.6302 20.7002H4.3702C3.7802 20.7002 3.3002 20.2202 3.3002 19.6302V4.3702C3.3002 3.7802 3.7802 3.3002 4.3702 3.3002H13.7002C14.1402 3.3002 14.5002 2.9402 14.5002 2.5002C14.5002 2.0602 14.1402 1.7002 13.7002 1.7002H4.3702C2.9002 1.7002 1.7002 2.9002 1.7002 4.3702Z" fill="#005537"/>
                        <path d="M10.3504 15.2901C9.94043 15.7001 9.39043 15.9301 8.82043 15.9301L7.31043 15.9501C6.84043 15.9501 6.39043 16.1401 6.06043 16.4701L5.11043 17.4201C4.80043 17.7301 4.80043 18.2401 5.11043 18.5501C5.27043 18.7101 5.47043 18.7801 5.68043 18.7801C5.89043 18.7801 6.09043 18.7001 6.25043 18.5501L7.20043 17.6001C7.20043 17.6001 7.28043 17.5501 7.33043 17.5501L8.84043 17.5301C9.84043 17.5301 10.7804 17.1301 11.4904 16.4201L19.3204 8.59008L21.5604 6.35008C21.8604 6.05008 22.0304 5.64008 22.0304 5.22008C22.0304 4.80008 21.8604 4.39008 21.5604 4.08008L19.6404 2.17008C19.0204 1.55008 18.0004 1.55008 17.3804 2.17008L15.0404 4.51008L7.29043 12.2601C6.69043 12.8601 6.31043 13.6501 6.21043 14.4801C6.16043 14.9201 6.47043 15.3201 6.91043 15.3701C6.94043 15.3701 6.98043 15.3701 7.01043 15.3701C7.41043 15.3701 7.75043 15.0701 7.80043 14.6701C7.86043 14.1901 8.08043 13.7301 8.43043 13.3801L15.7104 6.10008L17.6304 8.02008L10.3704 15.2801L10.3504 15.2901ZM18.5004 3.30008L20.4204 5.22008L18.7504 6.89008L16.8304 4.97008L18.5004 3.30008Z" fill="#005537"/>
                    </svg>
                </button>
            </div>

            <div class="StepSmeOffers__inner">
                <SmeOffers
                    v-if="offers"

                    :options="options"
                    :offers="offers"
                    :selected="options.offer?.price_warranty"

                    @select:tab="onSelectTab($event)"
                    @select="onSelectOffer($event)"
                    @code:loading="onCodeLoading"
                />

                <div class="StepSmeOffers__body">
                    <div v-if="!mobile" class="StepSmeOffers__usage-amount">
                        <div>dla szacowanego zużycia <b>{{ usageformat }} kWh/rok<sup>*</sup></b></div>

                        <button type="button" @click="onUsageClick">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M1.7002 4.3702V19.6302C1.7002 21.1002 2.9002 22.3002 4.3702 22.3002H19.6302C21.1002 22.3002 22.3002 21.1002 22.3002 19.6302V9.9802C22.3002 9.54019 21.9402 9.1802 21.5002 9.1802C21.0602 9.1802 20.7002 9.54019 20.7002 9.9802V19.6302C20.7002 20.2202 20.2202 20.7002 19.6302 20.7002H4.3702C3.7802 20.7002 3.3002 20.2202 3.3002 19.6302V4.3702C3.3002 3.7802 3.7802 3.3002 4.3702 3.3002H13.7002C14.1402 3.3002 14.5002 2.9402 14.5002 2.5002C14.5002 2.0602 14.1402 1.7002 13.7002 1.7002H4.3702C2.9002 1.7002 1.7002 2.9002 1.7002 4.3702Z" fill="#005537"/>
                                <path d="M10.3504 15.2901C9.94043 15.7001 9.39043 15.9301 8.82043 15.9301L7.31043 15.9501C6.84043 15.9501 6.39043 16.1401 6.06043 16.4701L5.11043 17.4201C4.80043 17.7301 4.80043 18.2401 5.11043 18.5501C5.27043 18.7101 5.47043 18.7801 5.68043 18.7801C5.89043 18.7801 6.09043 18.7001 6.25043 18.5501L7.20043 17.6001C7.20043 17.6001 7.28043 17.5501 7.33043 17.5501L8.84043 17.5301C9.84043 17.5301 10.7804 17.1301 11.4904 16.4201L19.3204 8.59008L21.5604 6.35008C21.8604 6.05008 22.0304 5.64008 22.0304 5.22008C22.0304 4.80008 21.8604 4.39008 21.5604 4.08008L19.6404 2.17008C19.0204 1.55008 18.0004 1.55008 17.3804 2.17008L15.0404 4.51008L7.29043 12.2601C6.69043 12.8601 6.31043 13.6501 6.21043 14.4801C6.16043 14.9201 6.47043 15.3201 6.91043 15.3701C6.94043 15.3701 6.98043 15.3701 7.01043 15.3701C7.41043 15.3701 7.75043 15.0701 7.80043 14.6701C7.86043 14.1901 8.08043 13.7301 8.43043 13.3801L15.7104 6.10008L17.6304 8.02008L10.3704 15.2801L10.3504 15.2901ZM18.5004 3.30008L20.4204 5.22008L18.7504 6.89008L16.8304 4.97008L18.5004 3.30008Z" fill="#005537"/>
                            </svg>
                        </button>
                    </div>

                    <SmeContactBox v-if="!mobile && bigusage" title="Masz duże zużycie energii!" content="Skontaktuj się z naszym Doradcą, który zaproponuje Ci indywidualne rozwiązanie lub skorzystaj z gotowej oferty online." :button="false" />
                    <SmeContactBox v-if="options.tariff == 'C12a'" :title="'Niska cena w obu strefach taryfy C12a!'" content="To nie błąd! U nas otrzymasz taką samą, niską cenę - w szczycie i poza szczytem." :button="false" />
                    <SmeContactBox v-if="options.tariff == 'C12b'" :title="'Niska cena w obu strefach taryfy C12b!'" content="To nie błąd! U nas otrzymasz taką samą, niską cenę - i w dzień, i w nocy." :button="false" />
                    <SmeContactBox v-if="!mobile && !options.savings && options.tariff == 'C11'" title="Twoja obecna cena energii nie jest zła!" content="Wybierając Respect Energy, możesz zamrozić stawkę nawet na kolejne 10 lat.<br><br>Ponadto możesz cieszyć się <b>brakiem opłat handlowych, wydłużonymi terminami płatności i 200 zł zniżki</b> na ofertę dla domu." :button="false" />

                    <template v-if="options.offer && options.savings">
                        <div class="contentbox contentbox--white">
                            <h2 v-if="bigusage" class="title-2">Skąd się biorą oszczędności?</h2>
                            <h2 v-if="!bigusage" class="title-2">Jak obliczyliśmy Twoje oszczędności?</h2>

                            <SmeCompareTable
                                :offer="options.offer"
                                :options="options"
                            />

                            <div class="StepSmeOffers__savings">
                                <div class="StepSmeOffers__savings-inner">
                                    <span>Suma szacunkowych oszczędności:</span>
                                    <b>{{ priceformat(options.savings.amount, false, 0) }} PLN<sup>*</sup></b>
                                </div>

                                <div v-if="options.offer?.cashback && options.code" class="StepSmeOffers__savings-cashback">
                                    +{{ priceformat(options.offer.cashback, false, 0) }} zł z kodu promocyjnego
                                </div>
                            </div>

                            <button type="button" class="StepSmeOffers__savings-more button button--link" @click="dialog_details.show()">*Więcej szczegółów</button>
                        </div>
                    </template>

                    <div v-if="options.offer" class="contentbox contentbox--white">
                        <OfferBoxDocuments
                            :offer="options.offer"
                            :codeitem="codeitem"
                            :dark="false"
                        />
                    </div>

                    <SmeContactBox button_label="Zamów rozmowę" @click:button="onContactClick" />
                </div>
            </div>

            <Dialog ref="dialog_usage" class="SmeDialog" title="Chcesz wgrać inną fakturę?">
                <p>Jeśli masz jakiekolwiek wątplwiości, możesz spróbować z innym dokumentem rozliczeniowym lub skontaktować się z nami.</p>

                <div class="StepUpload__box">
                    <UploadBox
                        :options="options"
                        :sme="true"

                        label_upload_mobile="Wgraj fakturę Twojej firmy"

                        @back="dialog_usage.close()"
                        @next="onUploadSubmit"

                        :visible_back="true"
                    />
                </div>

                <div class="SmeDialog__buttons">
                    <button type="button" class="button button--secondary button--full" @click="onContactClick">Zamów rozmowę</button>
                </div>
            </Dialog>

            <Dialog ref="dialog_contact" class="SmeDialog" title="Chcesz dowiedzieć się więcej?">
                <SmeSupportForm type="offer">
                    <p class="SmeSupportFormBox__text">Jeśli masz pytania albo potrzebujesz pomocy, wypełnij formularz. Nasz Doradca skontaktuje się z Tobą, aby dopasować najlepszą ofertę dla Twojej firmy.</p>
                </SmeSupportForm>
            </Dialog>

            <Dialog ref="dialog_details" class="SmeDialog StepSmeOffersDetails" title="Jak liczymy oszczędności?">
                <p>Nasze wyliczenia opierają się na zużyciu energii elektrycznej, które odczytaliśmy z Twojej faktury lub które zostało podane przez Ciebie w formularzu.</p>

                <ol>
                    <li>Wykorzystujemy ceny jednostkowe i opłaty handlowe pobrane z Twojej faktury.</li>
                    <li>Bierzemy pod uwagę Twoją taryfę oraz prognozowane zużycie na okres 2, 3, 5 lub 10 lat, aby obliczyć jak dużo zapłacisz i na tej podstawie zarekomendować Ci najlepszą możliwą ofertę.</li>
                    <li>Powyższe wyliczenia porównujemy z cenami gwarantowanymi przez Respect Energy.</li>
                    <li>Wszystkie wyliczenia są wartościami netto. Pamiętaj, że są one szacunkowe, a wysokość Twoich rachunków i oszczędności będzie zależała od realnego zużycia energii elektrycznej.</li>
                </ol>

                <hr>

                <h3>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M19.5397 5.34992C19.1897 5.61992 19.1197 6.11992 19.3797 6.46992C20.5897 8.06992 21.2197 9.98992 21.2197 11.9999C21.2197 17.0799 17.0797 21.2199 11.9997 21.2199C6.91969 21.2199 2.77969 17.0799 2.77969 11.9999C2.77969 6.91992 6.91969 2.77992 11.9997 2.77992C13.8497 2.77992 15.6297 3.31992 17.1597 4.34992C17.5297 4.59992 18.0197 4.49992 18.2697 4.12992C18.5197 3.75992 18.4197 3.26992 18.0497 3.01992C16.2597 1.80992 14.1697 1.16992 11.9997 1.16992C6.02969 1.17992 1.17969 6.02992 1.17969 11.9999C1.17969 17.9699 6.02969 22.8199 11.9997 22.8199C17.9697 22.8199 22.8197 17.9699 22.8197 11.9999C22.8197 9.63992 22.0697 7.38992 20.6597 5.50992C20.3997 5.15992 19.8997 5.08992 19.5397 5.34992Z" fill="#005537"/>
                        <path d="M10.7499 17.5902C11.1999 18.0202 11.7799 18.2502 12.3799 18.2502C12.6299 18.2502 12.8799 18.2102 13.1299 18.1302C13.5499 17.9902 13.7699 17.5402 13.6299 17.1202C13.4899 16.7002 13.0299 16.4702 12.6199 16.6202C12.2299 16.7502 11.9599 16.5402 11.8699 16.4502C11.7799 16.3602 11.5599 16.0902 11.6799 15.7002L13.3399 10.2502C13.4099 10.0102 13.3699 9.74021 13.2199 9.54021C13.0699 9.34021 12.8299 9.22021 12.5799 9.22021H10.8499C10.4099 9.22021 10.0499 9.58022 10.0499 10.0202C10.0499 10.4602 10.4099 10.8202 10.8499 10.8202H11.4999L10.1499 15.2302C9.88994 16.0702 10.1199 16.9802 10.7599 17.5902H10.7499Z" fill="#005537"/>
                        <path d="M14.03 6.70004C14.03 6.06004 13.51 5.54004 12.87 5.54004C12.23 5.54004 11.71 6.06004 11.71 6.70004C11.71 7.34004 12.23 7.86004 12.87 7.86004C13.51 7.86004 14.03 7.34004 14.03 6.70004Z" fill="#005537"/>
                    </svg>

                    O czym jeszcze trzeba wiedzieć?
                </h3>

                <p>Nasza oferta dotyczy zużycia energii elektrycznej, które średnio stanowi ok. 60% ceny na fakturze. Pozostała część to opłaty za dystrybucję energii elektrycznej, które zależą od Twojej lokalizacji. Nie mamy na nie wpływu i te wartości pozostaną niezmienne.</p>
                <p>Ponadto sprzedawcy energii elektrycznej pobierają opłaty handlowe i inne opłaty dodatkowe - mogą one wynosić od kilku do nawet kilkuset złotych miesięcznie! W Respect Energy opłata handlowa wynosi 0 zł (po wyrażeniu odpowiednich zgód)</p>
            </Dialog>
        </SmeLayout>
    `,

    emits: [
        'back',
        'offer',
    ],

    props: {
        options: { type: Object as PropType<SmeOrderJson>, required: true },
    },

    setup(props, ctx) {
        const loading = ref(false);
        const mobile = ref(false);
        const dialog_details = ref(null);
        const dialog_usage = ref(null);
        const dialog_contact = ref(null);

        const offers = ref(null);

        const usageformat = computed(() => {
            return Utils.usageFormat(props.options.usage);
        });

        const bigusage = computed(() => {
            return props.options.usage >= 500000;
        });

        const codeitem = computed(() => {
            return OfferUtils.getOfferCodeItem(props.options.code, props.options.offer?.id);
        });

        function priceformat(price, rounded, r = 3) {
            return Utils.priceFormat(price, rounded, '', r);
        }

        function onOfferClick() {
            Utils.scrollToEl($('.SmeOffers'));
        }

        async function onUsageClick() {
            dialog_usage.value.show();
        }

        async function onContactClick(e: MouseEvent) {
            e.preventDefault();

            dialog_contact.value.show();

            Tracking.doEvent('b2bsme_offer_call');
        }

        function onUploadSubmit() {
            Utils.setStep(props.options.nextstep);
        }

        async function onCodeLoading() {
            loading.value = true;
        }

        async function onSelectTab(price_warranty: number) {
            if (loading.value) {
                return;
            }

            loading.value = true;

            const r = await Utils.setCart('offer', {
                price_warranty: price_warranty,
            });

            Utils.setStep('sme.offer', r.options.offer);

            loading.value = false;
        }

        async function onSelectOffer(offer) {
            await Utils.setCart('offer', {
                offer_id: offer.id,
            });

            Tracking.doEvent('b2bsme_offer_start');

            ctx.emit('offer');
        }

        watch(() => props.options.code, async (a, b) => {
            if (a === null || b === null) {
                await reload();

                loading.value = false;
            }
        });

        async function reload() {
            const r = await Utils.get('/api/sme/offers');

            offers.value = r.offers;
        }

        function updateLayout() {
            mobile.value = window.innerWidth <= 768;
        }

        onMounted(() => {
            reload();

            updateLayout();
        });

        $(window).on('resize', updateLayout);

        Utils.attachBodyClass(['StepSmeOffers']);

        return {
            loading,
            mobile,
            dialog_details,
            dialog_usage,
            dialog_contact,

            offers,
            codeitem,

            usageformat,
            bigusage,

            priceformat,
            onOfferClick,
            onUsageClick,
            onContactClick,
            onUploadSubmit,
            onCodeLoading,
            onSelectTab,
            onSelectOffer,
        };
    },
});
